import React from 'react';
import { Box, FormControl, FormHelperText, InputLabel } from '@mui/material';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { colorPalette } from '@qualifio/ui-commons';

function ColumnsIcon({ columns, isSelected }: { columns: number; isSelected?: boolean }) {
  switch (columns) {
    case 1:
      return <OneColumnIcon isSelected={isSelected} />;
    case 2:
      return <TwoColumnsIcon isSelected={isSelected} />;
    case 3:
    default:
      return <ThreeColumnsIcon isSelected={isSelected} />;
  }
}

function OneColumnIcon({ isSelected }: { isSelected?: boolean }) {
  return (
    <svg width="35" height="35" viewBox="0 0 66 67" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="7" y="21" width="51" height="26" rx="3" fill={isSelected ? '#61C1DB' : '#BBBBBB'} />
    </svg>
  );
}

function TwoColumnsIcon({ isSelected }: { isSelected?: boolean }) {
  return (
    <svg width="35" height="35" viewBox="0 0 66 68" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="7" y="21" width="24" height="26" rx="3" fill={isSelected ? '#61C1DB' : '#BBBBBB'} />
      <rect
        x="33.5"
        y="21"
        width="24"
        height="26"
        rx="3"
        fill={isSelected ? '#61C1DB' : '#BBBBBB'}
      />
    </svg>
  );
}

function ThreeColumnsIcon({ isSelected }: { isSelected?: boolean }) {
  return (
    <svg width="35" height="35" viewBox="0 0 66 68" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="7" y="21" width="15" height="26" rx="3" fill={isSelected ? '#61C1DB' : '#BBBBBB'} />
      <rect
        x="24.667"
        y="21"
        width="15"
        height="26"
        rx="3"
        fill={isSelected ? '#61C1DB' : '#BBBBBB'}
      />
      <rect
        x="42.333"
        y="21"
        width="15"
        height="26"
        rx="3"
        fill={isSelected ? '#61C1DB' : '#BBBBBB'}
      />
    </svg>
  );
}

interface ColumnSelectFieldProps<FormValue extends FieldValues> {
  name: Path<FormValue>;
  control: Control<FormValue>;
  label: string;
  defaultValue?: any;
  helperText?: string;
}

export function ColumnSelectField<FormValue extends FieldValues>({
  name,
  control,
  label,
  defaultValue = 3,
  helperText,
}: ColumnSelectFieldProps<FormValue>) {
  const options = [1, 2, 3];

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
        <FormControl error={!!error} fullWidth>
          <InputLabel
            sx={theme => ({
              marginBottom: theme.spacing(0.4),
              transform: 'none',
              position: 'relative',
              userSelect: 'inherit',
            })}
          >
            {label}
          </InputLabel>

          <Box display="flex" gap={2} mt={1}>
            {options.map(option => {
              const isSelected = value === option;

              return (
                <Box
                  key={option}
                  onClick={() => onChange(option)}
                  sx={{
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: isSelected ? colorPalette.blue.main : colorPalette.grey.light,
                    borderRadius: 1,
                    padding: 1,
                    textAlign: 'center',
                    flex: 1,
                    transition: 'background-color 0.2s, border-color 0.2s',
                  }}
                >
                  <ColumnsIcon columns={option} isSelected={isSelected} />
                </Box>
              );
            })}
          </Box>

          {(error || helperText) && (
            <FormHelperText error>{error?.message || helperText}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}
